import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Eye, EyeOff, Mail, Lock, User, ArrowRight, ArrowLeft, 
  Loader2, Check, X, Leaf, TrendingUp, CheckCircle 
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { UserVertical } from '@/stores/authStore';
import { clsx } from 'clsx';

interface RegisterForm {
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
}

const verticals: { id: UserVertical; name: string; icon: any; description: string; color: string; modules: string[]; disabled?: boolean }[] = [
  {
    id: 'AGRO',
    name: 'Store Owner',
    icon: Leaf,
    description: 'Create and manage your online store. Perfect for small businesses and entrepreneurs.',
    color: 'from-green-500 to-emerald-600',
    modules: ['LabelForge', 'ShopFather', 'HTML Storefront'],
  },
  {
    id: 'INVESTOR',
    name: 'Enterprise',
    icon: TrendingUp,
    description: 'For businesses needing multiple stores and advanced features',
    color: 'from-zinc-600 to-zinc-700',
    modules: ['Dashboard', 'API Access', 'White-label'],
    disabled: true,
  },
];

export default function RegisterPage() {
  const [step, setStep] = useState<'vertical' | 'details' | 'success'>('vertical');
  const [selectedVertical, setSelectedVertical] = useState<UserVertical | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>();

  const password = watch('password', '');

  const passwordChecks = [
    { label: 'At least 8 characters', check: password.length >= 8 },
    { label: 'Contains uppercase', check: /[A-Z]/.test(password) },
    { label: 'Contains lowercase', check: /[a-z]/.test(password) },
    { label: 'Contains number', check: /[0-9]/.test(password) },
  ];

  const handleVerticalSelect = (vertical: UserVertical) => {
    setSelectedVertical(vertical);
    setStep('details');
  };

  const onSubmit = async (data: RegisterForm) => {
    if (!selectedVertical) {
      toast.error('Please select your business type');
      setStep('vertical');
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/auth/register', {
        email: data.email,
        password: data.password,
        name: data.name,
        vertical: selectedVertical,
      });

      setRegisteredEmail(data.email);
      setStep('success');
      toast.success('Registration successful! Check your email.');
    } catch (error: any) {
      const message = error.data?.message || 'Registration failed';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Success screen
  if (step === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center space-y-6"
      >
        {/* Success icon */}
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center">
            <CheckCircle className="text-green-500" size={40} />
          </div>
        </div>

        {/* Title */}
        <div>
          <h2 className="text-2xl font-bold text-white">Check Your Email</h2>
          <p className="mt-2 text-slate-400">
            We've sent a verification link to
          </p>
          <p className="mt-1 text-green-400 font-medium">
            {registeredEmail}
          </p>
        </div>

        {/* Instructions */}
        <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-left">
          <h3 className="font-medium text-slate-900 mb-2">Next steps:</h3>
          <ol className="space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-[#4A7C2E] text-white flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
              <span>Open the email from Green Gold</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-[#4A7C2E] text-white flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
              <span>Click the "Verify Email Address" button</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-[#4A7C2E] text-white flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
              <span>Start using the Green Gold platform</span>
            </li>
          </ol>
        </div>

        {/* Note */}
        <p className="text-sm text-slate-600">
          Didn't receive the email? Check your spam folder or{' '}
          <button 
            onClick={() => navigate('/login')}
            className="text-[#4A7C2E] hover:underline"
          >
            request a new link
          </button>
        </p>

        {/* Back to login */}
        <Link
          to="/login"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white hover:bg-gray-50 text-slate-700 font-medium transition-colors border border-gray-300"
        >
          <ArrowLeft size={16} />
          Back to Login
        </Link>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Logo - mobile only */}
      <div className="lg:hidden text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#4A7C2E] flex items-center justify-center">
          <span className="text-2xl font-bold text-white">GG</span>
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Green Gold</h1>
      </div>

      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-2">
        <div className={clsx(
          'w-3 h-3 rounded-full transition-colors',
          step === 'vertical' ? 'bg-[#4A7C2E]' : 'bg-gray-300'
        )} />
        <div className="w-8 h-0.5 bg-gray-300" />
        <div className={clsx(
          'w-3 h-3 rounded-full transition-colors',
          step === 'details' ? 'bg-[#4A7C2E]' : 'bg-gray-300'
        )} />
      </div>

      <AnimatePresence mode="wait">
        {step === 'vertical' ? (
          <motion.div
            key="vertical"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Header */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-900">Choose Your Business</h2>
              <p className="mt-2 text-slate-600">
                Select the type that best describes you
              </p>
            </div>

            {/* Vertical cards */}
            <div className="space-y-3">
              {verticals.map((vertical) => (
                <motion.button
                  key={vertical.id}
                  onClick={() => !vertical.disabled && handleVerticalSelect(vertical.id)}
                  whileHover={vertical.disabled ? {} : { scale: 1.02 }}
                  whileTap={vertical.disabled ? {} : { scale: 0.98 }}
                  disabled={vertical.disabled}
                  className={clsx(
                    'w-full p-4 rounded-xl border-2 text-left transition-all relative',
                    vertical.disabled
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                      : selectedVertical === vertical.id
                        ? 'border-[#4A7C2E] bg-green-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                  )}
                >
                  {vertical.disabled && (
                    <div className="absolute top-3 right-3 px-2 py-1 rounded bg-amber-50 border border-amber-200">
                      <span className="text-xs font-medium text-amber-700">Coming Soon</span>
                    </div>
                  )}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#4A7C2E] flex items-center justify-center flex-shrink-0">
                      <vertical.icon className="text-white" size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={clsx(
                        'font-semibold',
                        vertical.disabled ? 'text-slate-400' : 'text-slate-900'
                      )}>{vertical.name}</h3>
                      <p className={clsx(
                        'text-sm mt-1',
                        vertical.disabled ? 'text-slate-400' : 'text-slate-600'
                      )}>{vertical.description}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {vertical.modules.map((mod) => (
                          <span
                            key={mod}
                            className={clsx(
                              'px-2 py-0.5 text-xs rounded-full border',
                              vertical.disabled ? 'bg-gray-100 text-slate-400 border-gray-200' : 'bg-green-50 text-green-700 border-green-200'
                            )}
                          >
                            {mod}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="details"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            {/* Back button */}
            <button
              onClick={() => setStep('vertical')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft size={16} />
              <span>Change business type</span>
            </button>

            {/* Selected vertical badge */}
            {selectedVertical && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200">
                {(() => {
                  const v = verticals.find(v => v.id === selectedVertical);
                  if (!v) return null;
                  return (
                    <>
                      <div className="w-10 h-10 rounded-lg bg-[#4A7C2E] flex items-center justify-center">
                        <v.icon className="text-white" size={16} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Registering as</p>
                        <p className="font-medium text-slate-900">{v.name}</p>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Header */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Create Account</h2>
              <p className="mt-1 text-slate-600">
                Enter your details to get started
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Name */}
              <div>
                <label className="label">Name (optional)</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    {...register('name')}
                    className="input pl-12"
                    placeholder="John Doe"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="label">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                    className={`input pl-12 ${errors.email ? 'input-error' : ''}`}
                    placeholder="you@example.com"
                  />
                </div>
                {errors.email && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-1 text-sm text-red-400"
                  >
                    {errors.email.message}
                  </motion.p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="label">Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters',
                      },
                    })}
                    className={`input pl-12 pr-12 ${errors.password ? 'input-error' : ''}`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                
                {/* Password strength */}
                {password && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-3 grid grid-cols-2 gap-2"
                  >
                    {passwordChecks.map((item) => (
                      <div
                        key={item.label}
                        className={clsx(
                          'flex items-center gap-2 text-xs',
                          item.check ? 'text-green-400' : 'text-slate-500'
                        )}
                      >
                        {item.check ? <Check size={16} /> : <X size={16} />}
                        {item.label}
                      </div>
                    ))}
                  </motion.div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="label">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (value) =>
                        value === password || 'Passwords do not match',
                    })}
                    className={`input pl-12 ${errors.confirmPassword ? 'input-error' : ''}`}
                    placeholder="••••••••"
                  />
                </div>
                {errors.confirmPassword && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-1 text-sm text-red-400"
                  >
                    {errors.confirmPassword.message}
                  </motion.p>
                )}
              </div>

              {/* Submit */}
              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="btn-primary w-full py-3 text-lg"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin" size={24} />
                ) : (
                  <>
                    Create Account
                    <ArrowRight size={16} />
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <p className="text-center text-slate-600">
        Already have an account?{' '}
        <Link
          to="/login"
          className="text-[#4A7C2E] hover:text-[#3A5F1E] font-medium transition-colors"
        >
          Sign in
        </Link>
      </p>
    </div>
  );
}
